import katex from 'katex';

export type MathHealthSeverity = 'warning' | 'error';

export interface MathHealthIssue {
  code: string;
  message: string;
  severity: MathHealthSeverity;
  position?: number;
}

export interface MathHealthResult {
  ok: boolean;
  tex: string;
  displayMode: boolean;
  html: string;
  mathml: string;
  issues: MathHealthIssue[];
}

export interface MarkdownFormulaAudit {
  source: string;
  tex: string;
  displayMode: boolean;
  result: MathHealthResult;
}

export interface MarkdownMathHealthReport {
  healthy: boolean;
  formulaCount: number;
  invalidCount: number;
  warningCount: number;
  formulas: MarkdownFormulaAudit[];
}

export const FORMULA_HEALTH_LIMITS = Object.freeze({
  maxFormulaLength: 12_000,
  maxBraceDepth: 64,
  maxExpand: 200,
  maxSize: 50,
});

const SOFT_STRICT_CODES = new Set(['unknownSymbol', 'unicodeTextInMathMode']);
const FORBIDDEN_SOURCE = /(?:https?:\/\/|www\.|!\[|\]\(|<\s*(?:script|iframe|img|svg)\b|```|~~~|\u0000)/i;
const DANGEROUS_TEX = /\\(?:href|url|includegraphics|htmlClass|htmlId|htmlStyle|htmlData|def|gdef|edef|xdef|let|futurelet|newcommand|renewcommand|providecommand)\b/;

export function mathHealthStrictness(errorCode: string): 'ignore' | 'error' {
  return SOFT_STRICT_CODES.has(errorCode) ? 'ignore' : 'error';
}

export function validateTexFormula(rawTex: string, displayMode = false): MathHealthResult {
  const tex = String(rawTex || '').trim();
  const issues: MathHealthIssue[] = [];
  const fail = (code: string, message: string, position?: number): MathHealthResult => ({
    ok: false,
    tex,
    displayMode,
    html: '',
    mathml: '',
    issues: [...issues, { code, message, severity: 'error', position }],
  });

  if (!tex) return fail('empty-formula', '公式不能为空。');
  if (tex.length > FORMULA_HEALTH_LIMITS.maxFormulaLength) {
    return fail('formula-too-long', `公式长度超过 ${FORMULA_HEALTH_LIMITS.maxFormulaLength} 个字符。`);
  }
  if (FORBIDDEN_SOURCE.test(tex)) return fail('markdown-residue', '公式中包含 URL、Markdown 或 HTML 残片。');
  if (DANGEROUS_TEX.test(tex)) return fail('unsafe-command', '公式中包含不允许的链接、HTML、图片或宏定义命令。');

  const structure = validateStructure(tex);
  if (structure) return fail(structure.code, structure.message, structure.position);

  try {
    const html = katex.renderToString(tex, {
      displayMode,
      output: 'htmlAndMathml',
      throwOnError: true,
      trust: false,
      maxExpand: FORMULA_HEALTH_LIMITS.maxExpand,
      maxSize: FORMULA_HEALTH_LIMITS.maxSize,
      strict: (errorCode, errorMessage) => {
        const soft = SOFT_STRICT_CODES.has(errorCode);
        issues.push({ code: errorCode, message: errorMessage, severity: soft ? 'warning' : 'error' });
        return soft ? 'ignore' : 'error';
      },
    });
    const mathml = html.match(/<math\b[\s\S]*?<\/math>/i)?.[0] || '';
    if (!html.includes('class="katex')) return fail('missing-katex-root', 'KaTeX 输出缺少根节点。');
    if (!mathml) return fail('missing-mathml', 'KaTeX 输出缺少 MathML。');
    if (/katex-error|<merror\b/i.test(html)) return fail('error-node', '渲染结果包含错误节点。');
    if (!/<annotation\b[^>]*encoding="application\/x-tex"/i.test(mathml)) {
      return fail('missing-source-annotation', 'MathML 缺少原始 TeX 注解。');
    }
    return { ok: true, tex, displayMode, html, mathml, issues };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const position = parseErrorPosition(message);
    return fail('katex-parse-error', message, position);
  }
}

export function auditMathMarkdown(markdown: string): MarkdownMathHealthReport {
  const formulas: MarkdownFormulaAudit[] = [];
  processMathMarkdown(markdown, ({ source, tex, displayMode }) => {
    formulas.push({ source, tex, displayMode, result: validateTexFormula(tex, displayMode) });
    return source;
  });
  const invalidCount = formulas.filter((formula) => !formula.result.ok).length;
  const warningCount = formulas.reduce(
    (total, formula) => total + formula.result.issues.filter((issue) => issue.severity === 'warning').length,
    0,
  );
  return {
    healthy: invalidCount === 0,
    formulaCount: formulas.length,
    invalidCount,
    warningCount,
    formulas,
  };
}

export function quarantineInvalidMathMarkdown(markdown: string): string {
  return processMathMarkdown(markdown, ({ source, tex, displayMode }) => {
    const result = validateTexFormula(tex, displayMode);
    if (result.ok) return source;
    return displayMode ? fencedCode(tex, 'tex-invalid') : inlineCode(source);
  });
}

function processMathMarkdown(
  markdown: string,
  transform: (formula: { source: string; tex: string; displayMode: boolean }) => string,
): string {
  let value = String(markdown || '');
  const protectedValues: string[] = [];
  const protect = (content: string) => `\u0000FDMATHCODE${protectedValues.push(content) - 1}\u0000`;

  value = value.replace(/^ {0,3}(`{3,}|~{3,})[^\n]*\n[\s\S]*?^ {0,3}\1\s*$/gm, (match) => protect(match));
  value = value.replace(/(`+)[^\n]*?\1/g, (match) => protect(match));

  value = value.replace(/\$\$([\s\S]*?)\$\$/g, (source, tex: string) => protect(transform({ source, tex: tex.trim(), displayMode: true })));
  value = value.replace(/\$(?!\$)((?:\\.|[^$\n])+?)\$/g, (source, tex: string) => transform({ source, tex: tex.trim(), displayMode: false }));

  return value.replace(/\u0000FDMATHCODE(\d+)\u0000/g, (_match, index: string) => protectedValues[Number(index)] || '');
}

function validateStructure(tex: string): { code: string; message: string; position?: number } | null {
  let braceDepth = 0;
  let maxDepth = 0;
  const environments: Array<{ name: string; position: number }> = [];

  for (let index = 0; index < tex.length; index += 1) {
    if (isEscaped(tex, index)) continue;
    if (tex[index] === '{') {
      braceDepth += 1;
      maxDepth = Math.max(maxDepth, braceDepth);
      if (maxDepth > FORMULA_HEALTH_LIMITS.maxBraceDepth) {
        return { code: 'brace-depth-limit', message: '公式花括号嵌套过深。', position: index + 1 };
      }
    } else if (tex[index] === '}') {
      braceDepth -= 1;
      if (braceDepth < 0) return { code: 'unexpected-closing-brace', message: '公式包含多余的右花括号。', position: index + 1 };
    }
  }
  if (braceDepth !== 0) return { code: 'unbalanced-braces', message: '公式花括号不成对。' };

  for (const match of tex.matchAll(/\\(begin|end)\{([A-Za-z*]+)\}/g)) {
    const position = (match.index || 0) + 1;
    if (match[1] === 'begin') environments.push({ name: match[2], position });
    else {
      const opening = environments.pop();
      if (!opening || opening.name !== match[2]) {
        return { code: 'mismatched-environment', message: `公式环境 ${match[2]} 没有匹配的开始标记。`, position };
      }
    }
  }
  if (environments.length) {
    const opening = environments[environments.length - 1];
    return { code: 'unclosed-environment', message: `公式环境 ${opening.name} 没有结束标记。`, position: opening.position };
  }
  return null;
}

function inlineCode(value: string): string {
  const longest = Math.max(0, ...Array.from(value.matchAll(/`+/g), (match) => match[0].length));
  const fence = '`'.repeat(Math.max(1, longest + 1));
  return `${fence}${value}${fence}`;
}

function fencedCode(value: string, language: string): string {
  const longest = Math.max(0, ...Array.from(value.matchAll(/`+/g), (match) => match[0].length));
  const fence = '`'.repeat(Math.max(3, longest + 1));
  return `\n\n${fence}${language}\n${value}\n${fence}\n\n`;
}

function parseErrorPosition(message: string): number | undefined {
  const match = message.match(/(?:at position|position)\s+(\d+)/i);
  return match ? Number(match[1]) : undefined;
}

function isEscaped(value: string, index: number): boolean {
  let slashCount = 0;
  for (let cursor = index - 1; cursor >= 0 && value[cursor] === '\\'; cursor -= 1) slashCount += 1;
  return slashCount % 2 === 1;
}
